import React from 'react';

import { AnalysesContext } from './context';
import { loadAndCreateAnalyses as loadAndCreateAnalysesProcedure } from './procedures';
import { validateAnalysisConfig } from './procedures';
import { AnalysisConfig, AnalysisRef, ResourceAnalysis } from '../../types/analysis';

type ProviderProps<T extends string> = {
  config: AnalysisConfig<T>;
  resourceId: number | null;
  resourceType: ResourceAnalysis['item_type'];
  children: React.ReactNode;
};

const AnalysesContextProvider = <T extends string, TResource = any>({
  config,
  resourceId,
  resourceType,
  children,
}: ProviderProps<T>) => {
  React.useEffect(() => {
    validateAnalysisConfig(config);
  }, [config]);

  const [reducedAnalyses, setReducedAnalyses] = React.useState<AnalysisRef<T>[] | null>(null);
  const [loadingAnalyses, setLoadingAnalyses] = React.useState(false);

  const loadAndCreateAnalyses = React.useCallback(
    async (
      isSharedResource: boolean,
      resource: TResource,
      onError?: (_error: Error | unknown) => void
    ): Promise<AnalysisRef<T>[]> => {
      if (!resourceId || !resource) return [];
      setLoadingAnalyses(true);
      setReducedAnalyses(null);
      try {
        const analyses = await loadAndCreateAnalysesProcedure(
          config,
          resourceId,
          resourceType,
          isSharedResource,
          resource
        );
        setReducedAnalyses(analyses);
        return analyses;
      } catch (err) {
        if (onError) {
          onError(err);
        }
        setReducedAnalyses([]);
        throw err;
      } finally {
        setLoadingAnalyses(false);
      }
    },
    [config, resourceId, resourceType]
  );

  React.useEffect(
    () =>
      function cleanUp() {
        setReducedAnalyses(null);
        setLoadingAnalyses(false);
      },
    [resourceId]
  );

  const contextValue = React.useMemo(
    () => ({
      reducedAnalyses,
      loadingAnalyses,
      loadAndCreateAnalyses,
      resourceType,
      resourceId,
      config,
    }),
    [reducedAnalyses, loadingAnalyses, loadAndCreateAnalyses, resourceType, resourceId, config]
  );

  return <AnalysesContext.Provider value={contextValue}>{children}</AnalysesContext.Provider>;
};

AnalysesContextProvider.displayName = 'AnalysesContextProvider';

export { AnalysesContextProvider };
